import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiLogin, apiMe, apiChangePassword, apiAdminStats } from '../utils/api';
import { getAuthToken, saveAuthData, clearAuthData } from '../utils/auth';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [adminStats, setAdminStats] = useState({
    onlineUsers: 0,
    activeProjects: 0,
    totalUsers: 0,
    totalTimesheetReports: 0,
    totalOvertimeReports: 0,
    onlineUsersList: [],
    activeProjectsList: []
  });

  useEffect(() => {
    loadCurrentUser();
  }, []);

  const loadCurrentUser = async () => {
    const token = getAuthToken();
    if (!token) return;
    try {
      const data = await apiMe(token);
      if (data.success && data.user) {
        setIsLoggedIn(true);
        setUserRole(data.user.role);
        setCurrentUser(data.user);
        setMustChangePassword(!!data.user.mustChangePassword);
      } else {
        clearAuthData();
      }
    } catch {
      clearAuthData();
    }
  };

  const login = async (username, password) => {
    try {
      const data = await apiLogin(username, password);
      if (data.success && data.token) {
        saveAuthData(data.token, data.user);
        setIsLoggedIn(true);
        setUserRole(data.user.role);
        setCurrentUser(data.user);
        setMustChangePassword(!!data.user.mustChangePassword);
        setLoginError('');
        return { success: true };
      } else {
        setLoginError(data.error || 'Неверный логин или пароль');
        return { success: false, error: data.error };
      }
    } catch (error) {
      setLoginError('Ошибка подключения к серверу');
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    clearAuthData();
    setIsLoggedIn(false);
    setUserRole(null);
    setCurrentUser(null);
    setMustChangePassword(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    const token = getAuthToken();
    try {
      const data = await apiChangePassword(token, currentPassword, newPassword);
      if (data.success) {
        if (mustChangePassword) {
          setCurrentUser(prev => ({ ...prev, mustChangePassword: false }));
          setMustChangePassword(false);
        }
        return { success: true };
      } else {
        return { success: false, error: data.error };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const loadAdminStats = async () => {
    const token = getAuthToken();
    if (!token || userRole !== 'admin') return;
    try {
      const data = await apiAdminStats(token);
      if (data.success) {
        setAdminStats(data.stats);
      }
    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
    }
  };

  return (
    <AuthContext.Provider value={{
      isLoggedIn,
      userRole,
      currentUser,
      mustChangePassword,
      loginError,
      login,
      logout,
      changePassword,
      setLoginError,
      isChangingPassword, setIsChangingPassword,
      adminStats,
      loadAdminStats,
    }}>
      {children}
    </AuthContext.Provider>
  );
};